import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { axiosInstance } from "@/App";
import { Button } from "@/components/ui/button";
import { Calendar, MapPin, DollarSign, ArrowLeft, CreditCard, Ticket, Shield, Share2, Tag, X, Flag } from "lucide-react";
import { toast } from "sonner";
import AntiPiracy from "@/components/AntiPiracy";
import LiveStreamViewer from "@/components/LiveStreamViewer";
import LiveEventPreview from "@/components/LiveEventPreview";
import { formatEventLocal, getEventEndDate, zonedWallClockToDate } from "@/lib/eventTime";
import { LIVE_REPORT_REASONS } from "@/config/liveReportReasons";

const EventDetail = ({ user, onLogout }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [event, setEvent] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [purchasing, setPurchasing] = useState(false);
  const [hasTicket, setHasTicket] = useState(false);
  const [showingStream, setShowingStream] = useState(searchParams.get("watch") === "1");
  const [promoCode, setPromoCode] = useState("");
  const [promoDiscount, setPromoDiscount] = useState(null);
  const [promoValidating, setPromoValidating] = useState(false);
  const [showEventReport, setShowEventReport] = useState(false);
  const [eventReportReason, setEventReportReason] = useState("");
  const [eventReportSubmitting, setEventReportSubmitting] = useState(false);
  // Viewer device — chosen before EACH event; passed to the player so the
  // adaptive display engine can size the stream for the screen.
  const viewerDevice = null;

  const startWatching = () => {
    sessionStorage.removeItem(`viewer_device_${id}`);
    setShowingStream(true);
  };

  useEffect(() => {
    fetchEvent();
    if (user) {
      checkUserTicket();
    }
  }, [id, user]);

  const fetchEvent = async () => {
    try {
      const response = await axiosInstance.get(`/events/${id}`);
      setEvent(response.data);
    } catch (error) {
      console.error("Error fetching event:", error);
      toast.error("Event not found");
      navigate("/events");
    }
  };

  const checkUserTicket = async () => {
    try {
      const response = await axiosInstance.get("/tickets");
      const userTickets = response.data;
      const ticketForEvent = userTickets.find(t => t.event_id === id && !t.refunded);
      setHasTicket(!!ticketForEvent);
    } catch (error) {
      console.error("Error checking ticket:", error);
      setHasTicket(false);
    }
  };

  const submitEventReport = async () => {
    if (!user) {
      navigate("/");
      return;
    }
    setEventReportSubmitting(true);
    try {
      const { data } = await axiosInstance.post(`/events/${id}/report`, {
        reason: eventReportReason,
        target_type: "livestream",
      });
      setShowEventReport(false);
      setEventReportReason("");
      if (data?.refund?.status === "succeeded") {
        toast.success(`Event reported. Your $${Number(data.refund.amount || 0).toFixed(2)} ticket refund was issued.`);
      } else {
        toast.success("Event report submitted");
      }
      navigate("/my-tickets", { replace: true });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Event report could not be submitted");
    } finally {
      setEventReportSubmitting(false);
    }
  };

  const handlePurchase = async () => {
    if (!user) {
      // Store event ID to return to after signing in
      sessionStorage.setItem("pending_purchase_event", id);
      navigate("/");
      return;
    }

    setPurchasing(true);
    try {
      const coords = await getGeoCoords();
      // Use Stripe checkout for payment
      const response = await axiosInstance.post("/payments/checkout/session", {
        payment_type: "ticket",
        event_id: id,
        quantity: quantity,
        promo_code: promoDiscount?.code || null,
        origin_url: window.location.origin,
        user_lat: coords.lat,
        user_lon: coords.lon
      });

      if (!response.data.url && response.data.ticket_created) {
        toast.success("Promo applied — your ticket is ready!");
        setHasTicket(true);
        if (event?.status === "live") {
          setShowingStream(true);
        } else {
          navigate("/my-tickets");
        }
        return;
      }

      // Redirect to Stripe checkout
      window.location.href = response.data.url;
    } catch (error) {
      console.error("Error initiating checkout:", error);
      toast.error(error.response?.data?.detail || "Failed to initiate payment. Please try again.");
      setPurchasing(false);
    }
  };

  const applyPromoCode = async () => {
    if (!promoCode.trim()) {
      toast.error("Enter a promo code");
      return;
    }
    if (!user) {
      sessionStorage.setItem("pending_purchase_event", id);
      toast.info("Sign in to apply a promo code");
      navigate("/");
      return;
    }
    if (!event || Number(event.price || 0) <= 0) {
      toast.info("This event is already free");
      return;
    }

    setPromoValidating(true);
    try {
      const purchaseAmount = Number(event.price || 0) * quantity;
      const response = await axiosInstance.post("/promo-codes/validate", {
        code: promoCode.trim(),
        purchase_type: "ticket",
        purchase_amount: purchaseAmount,
      });
      setPromoDiscount(response.data);
      setPromoCode(response.data.code);
      toast.success(`Promo applied — you save $${Number(response.data.discount_amount || 0).toFixed(2)}`);
    } catch (error) {
      setPromoDiscount(null);
      toast.error(error.response?.data?.detail || "Invalid promo code");
    } finally {
      setPromoValidating(false);
    }
  };

  const clearPromoCode = () => {
    setPromoCode("");
    setPromoDiscount(null);
  };

  // Request the buyer's location for geo-fenced events. Resolves to nulls if
  // the event isn't restricted or the browser denies/​lacks geolocation — the
  // backend makes the final allow/deny decision.
  const getGeoCoords = () => {
    if (!event?.geo_restricted || !("geolocation" in navigator)) {
      return Promise.resolve({ lat: null, lon: null });
    }
    return new Promise((resolve) => {
      navigator.geolocation.getCurrentPosition(
        (pos) => resolve({ lat: pos.coords.latitude, lon: pos.coords.longitude }),
        () => resolve({ lat: null, lon: null }),
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
      );
    });
  };

  // Quick purchase without Stripe (free events)
  const handleFreePurchase = async () => {
    if (!user) {
      sessionStorage.setItem("pending_purchase_event", id);
      navigate("/");
      return;
    }

    setPurchasing(true);
    try {
      const coords = await getGeoCoords();
      await axiosInstance.post("/tickets", {
        event_id: id,
        quantity: quantity,
        user_lat: coords.lat,
        user_lon: coords.lon
      });
      toast.success("Tickets claimed successfully!");
      setHasTicket(true);
      if (event?.status === "live") {
        setShowingStream(true);
      } else {
        navigate("/my-tickets");
      }
    } catch (error) {
      console.error("Error claiming tickets:", error);
      toast.error(error.response?.data?.detail || "Failed to claim tickets");
    } finally {
      setPurchasing(false);
    }
  };

  if (!event) {
    return (
      <div className="min-h-screen bg-[#0f0f0f] flex items-center justify-center">
        <div className="text-white text-xl">Loading...</div>
      </div>
    );
  }

  // Religion events are always free — the price is locked and cannot be charged.
  const isReligion = (event.category || "").toLowerCase() === "religion";
  const effectivePrice = isReligion ? 0 : event.price;
  const isFreeEvent = effectivePrice === 0;
  const originalTotal = effectivePrice * quantity;
  const discountedTotal = promoDiscount ? Number(promoDiscount.final_price || 0) : originalTotal;
  const totalPrice = discountedTotal.toFixed(2);

  // Check if event is live and user has access
  const isLive = event.status === "live";
  // Event has ended → no more ticket sales. Mirrors backend is_event_ended:
  // completed/cancelled status, or the end (or start+2h) datetime is past.
  const hasEnded = (() => {
    if (event.status === "completed" || event.status === "cancelled") return true;
    if (isLive) return false;
    if (!event.date) return false;
    const timeStr = event.start_time || event.time || "7:00 PM";
    const dt = event.end_time
      ? getEventEndDate(event)
      : zonedWallClockToDate(event.date, timeStr, event.timezone);
    if (!dt) return false;
    // No explicit end time → assume a 2-hour show before calling it over.
    const endMs = event.end_time ? dt.getTime() : dt.getTime() + 2 * 60 * 60 * 1000;
    return Date.now() > endMs;
  })();
  // ALL viewers must be logged in
  // For free events: user must be logged in (ticket auto-claimed or not required)
  // For paid events: user must be logged in and have a ticket
  const canWatchStream = isLive && user && (isFreeEvent || hasTicket);

  // If user is actively viewing the stream, show full-screen viewer
  if (canWatchStream && showingStream) {
    return (
      <AntiPiracy enabled={true}>
        <div className="h-screen w-full bg-black" data-testid="live-stream-fullscreen">
          <LiveStreamViewer
            eventId={id}
            userId={user.id}
            userName={user.username || user.name || user.email}
            userPicture={user.picture}
            event={event}
            viewerDevice={viewerDevice}
          />
        </div>
      </AntiPiracy>
    );
  }

  return (
    <AntiPiracy enabled={isLive}>
      <div className="min-h-screen bg-[#0f0f0f]" data-testid="event-detail-page">
        <Navbar user={user} onLogout={onLogout} />

        {showEventReport && (
          <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/75 p-4" onClick={() => setShowEventReport(false)}>
            <div className="w-full max-w-sm rounded-2xl border border-white/10 bg-[#12151d] p-5" onClick={(clickEvent) => clickEvent.stopPropagation()}>
              <div className="mb-4 flex items-center justify-between">
                <h2 className="text-lg font-black text-white">Report this event</h2>
                <button type="button" onClick={() => setShowEventReport(false)} aria-label="Close report dialog"><X className="h-5 w-5 text-gray-400" /></button>
              </div>
              <div className="space-y-2">
                {LIVE_REPORT_REASONS.map((reason) => (
                  <button
                    key={reason.value}
                    type="button"
                    onClick={() => setEventReportReason(reason.value)}
                    className={`w-full rounded-lg border px-3 py-2 text-left text-sm ${eventReportReason === reason.value ? "border-red-400/50 bg-red-500/20 text-white" : "border-white/10 bg-white/5 text-gray-200"}`}
                  >
                    {reason.label}
                  </button>
                ))}
              </div>
              <Button onClick={submitEventReport} disabled={!eventReportReason || eventReportSubmitting} className="mt-4 w-full bg-red-600 hover:bg-red-700">
                {eventReportSubmitting ? "Submitting…" : "Submit report"}
              </Button>
            </div>
          </div>
        )}
        
        <div className="pt-20">
          {/* Live Stream or Hero Image */}
          {isLive ? (
            <div className="relative h-[60vh] w-full bg-black">
              {/* Placeholder for users without tickets */}
              <div className="w-full h-full flex items-center justify-center bg-gradient-to-b from-gray-900 to-black">
                <div className="text-center">
                  <div className="flex items-center justify-center gap-2 mb-4">
                    <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
                    <span className="text-red-500 font-bold text-lg">LIVE NOW</span>
                  </div>
                  <LiveEventPreview
                    eventId={id}
                    startedAt={event.started_at || event.stream_ready_at}
                    imageUrl={event.image_url}
                    title={event.title}
                    viewerIdentity={user?.id}
                  />
                  {!user ? (
                    <p className="text-yellow-400 mt-4 text-sm">
                      Sign in and purchase a ticket to watch this live event
                    </p>
                  ) : !hasTicket ? (
                    <p className="text-yellow-400 mt-4 text-sm">
                      Purchase a ticket below to watch this live stream
                    </p>
                  ) : null}
                  <p className="text-gray-400 mt-2 text-sm flex items-center justify-center gap-2">
                    <Shield className="w-4 h-4" />
                    Content protected • Recording disabled
                  </p>
                </div>
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-[#0f0f0f] via-transparent to-transparent pointer-events-none"></div>
              
              <button
                onClick={() => navigate(-1)}
                className="absolute top-8 left-8 bg-black/60 hover:bg-blue-600/80 text-white p-3 rounded-full transition-colors z-10"
                data-testid="back-button"
              >
                <ArrowLeft className="w-6 h-6" />
              </button>
            </div>
          ) : (
            <div className="relative h-[60vh] w-full">
              <img 
                src={event.image_url} 
                alt={event.title}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0f0f0f] via-black/50 to-transparent"></div>
              
              <button
                onClick={() => navigate(-1)}
                className="absolute top-8 left-8 bg-black/60 hover:bg-blue-600/80 text-white p-3 rounded-full transition-colors"
                data-testid="back-button"
              >
                <ArrowLeft className="w-6 h-6" />
              </button>
            </div>
          )}

        {/* Event Details */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-32 relative z-10 pb-20">
          <div className="grid md:grid-cols-3 gap-8">
            <div className="md:col-span-2">
              <h1 className="text-white text-4xl md:text-6xl font-black mb-6" data-testid="event-detail-title">{event.title}</h1>
              
              <div className="flex flex-wrap gap-6 mb-8">
                <div className="flex items-center gap-2 text-gray-300">
                  <Calendar className="w-5 h-5" />
                  <span data-testid="event-date">{formatEventLocal(event)}</span>
                </div>
                <div className="flex items-center gap-2 text-gray-300">
                  <MapPin className="w-5 h-5" />
                  <span data-testid="event-venue">{event.venue}</span>
                </div>
              </div>

              <div className="bg-transparent rounded-lg p-6 mb-6">
                <h2 className="text-white text-2xl font-bold mb-4">About</h2>
                <p className="text-gray-300 text-lg leading-relaxed" data-testid="event-description">{event.description}</p>
              </div>

              <div className="flex flex-wrap items-center gap-3">
                <div className="inline-block bg-blue-600/20 border border-blue-600 rounded-lg px-4 py-2">
                  <span className="text-blue-400 font-medium">{event.category}</span>
                </div>
                <div className="inline-flex items-center gap-2 bg-white/5 border border-white/15 rounded-lg px-4 py-2" data-testid="event-orientation">
                  <span className="inline-block h-3.5 w-3.5 rounded-full border border-gray-300" />
                  <span className="text-gray-200 font-medium">Automatic display</span>
                </div>
                <button
                  type="button"
                  onClick={() => user ? setShowEventReport(true) : navigate("/")}
                  className="inline-flex items-center gap-2 rounded-lg border border-red-400/20 bg-red-500/10 px-4 py-2 text-sm font-semibold text-red-200 hover:bg-red-500/20"
                  data-testid="report-event-button"
                >
                  <Flag className="h-4 w-4" />
                  Report event
                </button>
              </div>
            </div>

            {/* Purchase Card */}
            <div className="md:col-span-1">
              <div className="bg-gray-900/80 backdrop-blur-md rounded-lg p-6 sticky top-24" data-testid="purchase-card">
                {/* Live status heading; the purchase-card action below remains clickable. */}
                {canWatchStream && (
                  <div className="mb-6">
                    <div
                      className="flex items-center justify-center gap-3 py-3"
                      data-testid="watch-live-status"
                    >
                      <span className="relative flex h-3.5 w-3.5" aria-hidden="true">
                        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-400 opacity-75" />
                        <span className="relative inline-flex h-3.5 w-3.5 rounded-full bg-red-500 shadow-[0_0_18px_rgba(239,68,68,1)]" />
                      </span>
                      <span
                        className="text-xl font-black tracking-wide text-red-100"
                        style={{ textShadow: "0 0 8px rgba(248,113,113,.95), 0 0 22px rgba(239,68,68,.8)" }}
                      >
                        Watch Live Now
                      </span>
                    </div>
                    <p className="text-gray-400 text-xs text-center mt-2">
                      {isFreeEvent ? "Free event - no ticket needed" : "You have a ticket"}
                    </p>
                  </div>
                )}
                
                {/* Show Share button when live and user has ticket */}
                {isLive && hasTicket ? (
                  <>
                    <div className="text-center mb-6">
                      <div className="w-16 h-16 bg-green-600/20 rounded-full flex items-center justify-center mx-auto mb-4">
                        <Ticket className="w-8 h-8 text-green-400" />
                      </div>
                      <h3 className="text-white text-xl font-bold">You Have Access!</h3>
                      <p className="text-gray-400 text-sm mt-2">Share this event with friends!</p>
                    </div>

                    <Button
                      onClick={startWatching}
                      className="mb-4 w-full bg-red-600 hover:bg-red-500 text-white font-bold text-lg py-6 transition-colors flex items-center justify-center gap-2 shadow-[0_0_34px_rgba(239,68,68,.8)]"
                      data-testid="ticket-holder-watch-button"
                    >
                      <span className="w-3 h-3 bg-white rounded-full animate-pulse" />
                      Watch Live Now
                    </Button>
                    
                    <Button
                      onClick={() => {
                        const shareUrl = `${window.location.origin}/event/${id}`;
                        const shareText = `Check out "${event.title}" on ShowMeLive! ${shareUrl}`;
                        
                        if (navigator.share) {
                          navigator.share({
                            title: event.title,
                            text: `Watch "${event.title}" live on ShowMeLive!`,
                            url: shareUrl
                          }).catch(console.error);
                        } else {
                          navigator.clipboard.writeText(shareText);
                          toast.success("Link copied to clipboard!");
                        }
                      }}
                      className="w-full bg-purple-600 hover:bg-purple-700 text-white font-bold text-lg py-6 transition-colors flex items-center justify-center gap-2"
                      data-testid="share-button"
                    >
                      <Share2 className="w-5 h-5" />
                      Share This Show
                    </Button>
                    
                    <div className="mt-4 grid grid-cols-3 gap-2">
                      <button
                        onClick={() => window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(`Watch "${event.title}" live on ShowMeLive!`)}&url=${encodeURIComponent(`${window.location.origin}/event/${id}`)}`, '_blank')}
                        className="bg-gray-800 hover:bg-gray-700 p-3 rounded-lg text-gray-300 transition-colors"
                      >
                        𝕏
                      </button>
                      <button
                        onClick={() => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(`${window.location.origin}/event/${id}`)}`, '_blank')}
                        className="bg-gray-800 hover:bg-gray-700 p-3 rounded-lg text-blue-400 transition-colors"
                      >
                        f
                      </button>
                      <button
                        onClick={() => window.open(`https://wa.me/?text=${encodeURIComponent(`Watch "${event.title}" live on ShowMeLive! ${window.location.origin}/event/${id}`)}`, '_blank')}
                        className="bg-gray-800 hover:bg-gray-700 p-3 rounded-lg text-green-400 transition-colors"
                      >
                        ✓
                      </button>
                    </div>
                    
                    <p className="text-gray-500 text-xs text-center mt-4">
                      Help your friends discover amazing live content
                    </p>
                  </>
                ) : (
                  <>
                    {/* Show purchase options - available even during live events */}
                    <div className="flex items-center justify-between mb-6">
                      <div className="flex items-center gap-2">
                        <DollarSign className="w-6 h-6 text-green-500" />
                        <span className="text-white text-3xl font-bold" data-testid="event-price">
                          {isFreeEvent ? "FREE" : `$${effectivePrice}`}
                        </span>
                      </div>
                      {!isFreeEvent && <span className="text-gray-400">per ticket</span>}
                    </div>

                    {isLive && !isFreeEvent && (
                      <div className="bg-yellow-600/20 border border-yellow-600 rounded-lg p-3 mb-4">
                        <p className="text-yellow-400 text-sm text-center">
                          🔴 Event is LIVE! Purchase now to watch instantly
                        </p>
                      </div>
                    )}

                    <div className="mb-5 rounded-xl border border-white/10 bg-black/20 p-3" data-testid="event-promo-section">
                      <div className="mb-2 flex items-center justify-between gap-3">
                        <div className="flex items-center gap-2 text-sm font-semibold text-gray-200">
                          <Tag className="h-4 w-4 text-blue-400" />
                          Promo code
                        </div>
                        {promoDiscount && (
                          <button onClick={clearPromoCode} className="flex items-center gap-1 text-xs font-semibold text-gray-500 hover:text-white" aria-label="Remove promo code">
                            <X className="h-3.5 w-3.5" /> Remove
                          </button>
                        )}
                      </div>
                      {promoDiscount ? (
                        <div className="rounded-lg border border-green-500/25 bg-green-500/10 px-3 py-2.5">
                          <div className="flex items-center justify-between gap-3">
                            <span className="font-mono text-sm font-bold tracking-wide text-green-300">{promoDiscount.code}</span>
                            <span className="text-sm font-bold text-green-400">-${Number(promoDiscount.discount_amount || 0).toFixed(2)}</span>
                          </div>
                          <p className="mt-1 text-xs text-green-200/60">{promoDiscount.description || "Discount applied"}</p>
                        </div>
                      ) : (
                        <div className="flex gap-2">
                          <input
                            value={promoCode}
                            onChange={(inputEvent) => setPromoCode(inputEvent.target.value.toUpperCase())}
                            onKeyDown={(keyboardEvent) => {
                              if (keyboardEvent.key === "Enter") {
                                keyboardEvent.preventDefault();
                                applyPromoCode();
                              }
                            }}
                            disabled={isFreeEvent || hasEnded || promoValidating}
                            placeholder={isFreeEvent ? "Event is already free" : "Enter promo code"}
                            className="h-10 min-w-0 flex-1 rounded-lg border border-white/10 bg-white/[.04] px-3 font-mono text-sm uppercase text-white outline-none placeholder:font-sans placeholder:normal-case placeholder:text-gray-600 focus:border-blue-500 disabled:cursor-not-allowed disabled:opacity-60"
                            data-testid="event-promo-input"
                            aria-label="Promo code"
                          />
                          <button
                            onClick={applyPromoCode}
                            disabled={isFreeEvent || hasEnded || promoValidating || !promoCode.trim()}
                            className="h-10 rounded-lg border border-blue-500/30 bg-blue-500/15 px-4 text-sm font-bold text-blue-300 transition-colors hover:bg-blue-500/25 disabled:cursor-not-allowed disabled:opacity-40"
                            data-testid="event-promo-apply"
                          >
                            {promoValidating ? "Checking…" : "Apply"}
                          </button>
                        </div>
                      )}
                    </div>

                    <div className="border-t border-gray-700 pt-4 mb-6">
                      {promoDiscount && (
                        <div className="mb-2 flex items-center justify-between text-sm">
                          <span className="text-gray-500">Original total</span>
                          <span className="text-gray-400 line-through">${originalTotal.toFixed(2)}</span>
                        </div>
                      )}
                      <div className="flex items-center justify-between text-lg">
                        <span className="text-gray-300">Total</span>
                        <span className="text-white font-bold text-2xl" data-testid="total-price">
                          {isFreeEvent ? "FREE" : `$${totalPrice}`}
                        </span>
                      </div>
                    </div>

                    <Button
                      onClick={isLive && canWatchStream
                        ? startWatching
                        : (isFreeEvent ? handleFreePurchase : handlePurchase)}
                      disabled={purchasing || hasEnded}
                      className={`w-full disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold text-lg py-6 transition-colors flex items-center justify-center gap-2 ${
                        isLive
                          ? "bg-red-600 hover:bg-red-500 shadow-[0_0_34px_rgba(239,68,68,.8)]"
                          : "bg-blue-600 hover:bg-blue-700"
                      }`}
                      data-testid="purchase-button"
                    >
                      {hasEnded ? (
                        "Event Ended"
                      ) : purchasing ? (
                        "Processing..."
                      ) : isLive ? (
                        <>
                          <span className="w-3 h-3 bg-white rounded-full animate-pulse" />
                          Watch Live Now
                        </>
                      ) : user ? (
                        <>
                          {isFreeEvent ? (
                            <>
                              <Ticket className="w-5 h-5" />
                              Claim Free Tickets
                            </>
                          ) : (
                            <>
                              <CreditCard className="w-5 h-5" />
                              {discountedTotal === 0 ? "Claim with Promo" : `Pay $${totalPrice}`}
                            </>
                          )}
                        </>
                      ) : (
                        "Sign in to Purchase"
                      )}
                    </Button>
                    
                    {!isFreeEvent && (
                      <p className="text-gray-500 text-xs text-center mt-3">
                        Secure payment powered by Stripe
                      </p>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
        </div>
      </div>
    </AntiPiracy>
  );
};

export default EventDetail;
